import * as Sentry from "@sentry/vue"
import type { App } from "vue"
import type { Router } from "vue-router"
import { isNavigationFailure } from "vue-router"
import errorTypeConst from "@common_lib/base/constants/errorTypeConst"
import { tryReloadOnChunkError } from "@/utils/chunkLoad"

const IS_DEV = import.meta.env.MODE === "dev" || import.meta.env.MODE === "dev-nginx"
const IS_PROD = import.meta.env.MODE === "prod"

const PROD_CUSTOM_CONFIG = {
  sampleRate: 0.005,
  tracesSampleRate: 0,
  maxBreadcrumbs: 10,
  normalizeDepth: 3
}

const UAT_CUSTOM_CONFIG = {
  sampleRate: 1,
  tracesSampleRate: 1,
  maxBreadcrumbs: 100,
  normalizeDepth: 5
}

const CUSTOM_CONFIG = IS_PROD ? PROD_CUSTOM_CONFIG : UAT_CUSTOM_CONFIG

const IGNORE_ERRORS = [
  "error occurred",
  "request canceled",
  "network error",
  /unable to preload/i,
  "locale messages",
  /the play\(\) request was interrupted/i, // jbl.js media error
  "AxiosError: Request aborted",
  "AxiosError: Network Error",
  "mini game webSocket is not connected",
  /(?:Error invoking \w+: )?Method not found/i, // JBL WebSocket method not found
  "JBLWebSocket is not defined",
  /Can't find variable: JBLWebSocket/i,
  "cordova is not defined",
  /Can't find variable: cordova/i,
  // Third-party browser-injected scripts (Samsung Internet, X Browser, etc.)
  "swbrowser is not defined",
  /Can't find variable: swbrowser/i,
  "xbrowser is not defined",
  /Can't find variable: xbrowser/i,
  // Benign browser warnings: ResizeObserver aborts a layout loop without
  // breaking functionality; W3C and Chromium classify these as safe to ignore.
  /ResizeObserver loop limit exceeded/,
  /ResizeObserver loop completed with undelivered notifications/
]

// 建立排除的錯誤條件列表（status code 和 message 都納入比對）
const EXCLUDED_ERROR_MATCHERS = Object.values(errorTypeConst)
  .filter(errorInfo => errorInfo.excludeFromSentry)
  .flatMap(errorInfo => [errorInfo.status, errorInfo.message].filter(Boolean) as string[])

const DENY_URLS = [
  /extensions\//i,
  /^chrome:\/\//i,
  /jbl\.js/i,
  "player-min.js",
  /analysis\.qn32\.com/i,
  /akamaized\.net/i, // Betradar LMT widget external errors
  /freshchat\.com/i, // FreshChat widget JSON parse errors
  /ucbrowser_script\.js/i // UCBrowser injected script errors
]

const errorCounts = new Map()
const ERROR_THRESHOLD = 5

const initSentry = ({ app, router }: { app: App; router: Router }) => {
  if (IS_DEV) return

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration({ router })],
    denyUrls: DENY_URLS,
    ignoreErrors: IGNORE_ERRORS,
    beforeSend(event, hint) {
      // vue-router rejects the navigation promise on next(false) /
      // next({...}) by spec; these are flow-control signals, not bugs.
      if (isNavigationFailure(hint?.originalException)) {
        return null
      }

      if (!event.exception?.values) return event

      const errorMessage = event.exception.values[0].value

      if (!errorMessage) return event

      // 檢查是否為需要排除的錯誤（比對 status code 和 message）
      const shouldExclude = EXCLUDED_ERROR_MATCHERS.some((matcher: string) =>
        errorMessage.includes(matcher)
      )

      if (shouldExclude) {
        return null
      }

      // Chunk load errors are caused by deploy cache invalidation (old chunk URLs no longer exist).
      // Router-level errors are auto-reloaded via router.onError; Vue async component errors
      // bypass the router and arrive here directly, so we trigger the same one-shot reload here.
      if (tryReloadOnChunkError(errorMessage)) {
        return null
      }

      const count = (errorCounts.get(errorMessage) || 0) + 1
      errorCounts.set(errorMessage, count)

      if (count > ERROR_THRESHOLD) {
        return null
      }

      return event
    },
    ...CUSTOM_CONFIG
  })
}

export default initSentry
